import { useState } from "react";
import { X, Send, Eye, Edit3, Users } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { toast } from "sonner";
import { RecipientPicker } from "./RecipientPicker";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * EmailComposer — Mahra-branded email composer for admin.
 * Supports single or bulk recipients, custom from address, subject, rich body, and preview.
 * Section 7 (Recipient Selector) — in single-recipient mode, admin can look up a
 * Customer/Booking/Trial to auto-populate the recipient and persist the linked
 * entity IDs on the sent communication (via `/admin/follow-up/email/send`).
 */
export default function EmailComposer({ toEmail, toName, recipients, onClose }) {
  const isBulk = recipients && recipients.length > 0;
  const [form, setForm] = useState({
    to_email: toEmail || "",
    // v6.3 — default sender is hello@ (admin@ removed from options because
    // that inbox is not monitored by the owner and replies would be lost).
    from_email: "hello@shopmahra.com.au",
    subject: "",
    body: "",
  });
  const [preview, setPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendProgress, setSendProgress] = useState({ sent: 0, total: 0 });
  // Section 7 — picked recipient (single-recipient mode only). When set, the
  // send routes through the branded follow-up endpoint so the linkage sticks.
  const [pickedRecipient, setPickedRecipient] = useState(null);

  const handlePick = (r) => {
    setPickedRecipient(r);
    setForm(f => ({ ...f, to_email: r.email || f.to_email }));
  };
  const handleClearPick = () => {
    setPickedRecipient(null);
  };

  // Address-change guard — same rationale as ComposeMessageModal: if admin
  // manually edits the To address after picking a recipient, the linked
  // Customer/Booking/Trial is no longer trustworthy, so we auto-clear it and
  // warn once. Explicit opt-in for proxy-address sending is a future task.
  const _emailsMatch = (a, b) => (a || "").trim().toLowerCase() === (b || "").trim().toLowerCase();
  const handleToEmailChange = (nextValue) => {
    setForm(f => ({ ...f, to_email: nextValue }));
    if (pickedRecipient && !_emailsMatch(nextValue, pickedRecipient.email)) {
      setPickedRecipient(null);
      toast.warning("Recipient details were changed. The customer and booking links have been cleared.");
    }
  };

  const handleSend = async () => {
    if (sending) return;                                  // Section 19 — prevent duplicate sends from repeated taps
    if (!isBulk && !form.to_email) { toast.error("Recipient email required"); return; }
    if (!form.subject || !form.body) { toast.error("Please fill in subject and message"); return; }
    setSending(true);

    const token = localStorage.getItem("adminToken");
    const buildHtml = (name) => {
      const bodyHtml = form.body.split("\n").map(line =>
        line.trim() ? `<p style="font-family:'Montserrat',Helvetica,Arial,sans-serif;font-size:14px;color:#4A4A4A;line-height:1.7;margin:0 0 12px;">${line}</p>` : `<br/>`
      ).join("");
      const greeting = name ? `<p style="font-family:'Cormorant Garamond',Georgia,'Times New Roman',serif;font-size:22px;color:#1A1A1A;margin:0 0 16px;font-weight:400;">Dear ${name},</p>` : "";
      return greeting + bodyHtml;
    };

    try {
      if (isBulk) {
        setSendProgress({ sent: 0, total: recipients.length });
        let sent = 0;
        let failed = 0;
        for (const r of recipients) {
          try {
            const res = await fetch(`${API}/api/admin/analytics/send-custom-email`, {
              method: "POST",
              headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
              body: JSON.stringify({ to_email: r.email, from_email: form.from_email, subject: form.subject, body_html: buildHtml(r.name) }),
            });
            if (res.ok) sent++; else failed++;
          } catch { failed++; }
          setSendProgress(p => ({ ...p, sent: sent + failed }));
        }
        if (failed > 0) toast.error(`${failed} of ${recipients.length} emails failed`);
        else toast.success(`Sent to ${sent} recipients`);
      } else {
        // Single recipient: when the admin has picked a linked recipient via the
        // Recipient Selector, route through the branded follow-up endpoint so
        // customer_id / booking_id / trial_lead_id / appointment_id all persist
        // on the sent email_log row. Otherwise fall back to the legacy custom-email
        // endpoint for backward compat with existing entry points.
        if (pickedRecipient) {
          const token = localStorage.getItem("adminToken");
          const res = await fetch(`${API}/api/admin/follow-up/email/send`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            body: JSON.stringify({
              to_email: form.to_email,
              subject: form.subject,
              body: form.body,
              recipient_name: pickedRecipient.label?.split(" · ")[0] || toName || "",
              customer_id: pickedRecipient.customer_id || "",
              booking_id: pickedRecipient.booking_id || "",
              trial_lead_id: pickedRecipient.trial_lead_id || "",
              appointment_id: pickedRecipient.appointment_id || "",
              template_key: "admin_email_composer",
            }),
          });
          if (!res.ok) throw new Error(await res.text());
          toast.success(`Email sent to ${form.to_email}`);
        } else {
          const res = await fetch(`${API}/api/admin/analytics/send-custom-email`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            body: JSON.stringify({ to_email: form.to_email, from_email: form.from_email, subject: form.subject, body_html: buildHtml(toName) }),
          });
          if (!res.ok) throw new Error(await res.text());
          toast.success(`Email sent to ${form.to_email}`);
        }
      }
      onClose();
    } catch (e) {
      toast.error("Failed to send: " + e.message);
    } finally {
      setSending(false);
    }
  };

  const displayName = isBulk ? recipients[0]?.name : toName;

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center" data-testid="email-composer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white sm:rounded-lg shadow-xl w-full sm:max-w-lg sm:mx-4 max-h-[92vh] sm:max-h-[90vh] flex flex-col overflow-hidden rounded-t-2xl sm:rounded-t-lg">
        {/* Header */}
        <div className="bg-[#C9A0A0] px-6 py-4 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-white font-light tracking-[4px] text-lg" style={{ fontFamily: "'Cormorant Garamond', serif" }}>MAHRA</span>
            {isBulk && <span className="bg-white/20 text-white text-[10px] px-2 py-0.5 rounded-full tracking-wider"><Users className="w-3 h-3 inline mr-1" />{recipients.length} recipients</span>}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setPreview(p => !p)} className="p-1.5 rounded hover:bg-white/20 text-white" title={preview ? "Edit" : "Preview"} data-testid="toggle-preview">
              {preview ? <Edit3 className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
            <button onClick={onClose} className="p-1.5 rounded hover:bg-white/20 text-white"><X className="w-4 h-4" /></button>
          </div>
        </div>

        {preview ? (
          <div className="flex-1 overflow-y-auto p-6 pb-0" data-testid="email-preview">
            <div className="bg-[#F5F3F0] rounded-lg p-6">
              <div className="bg-white max-w-md mx-auto" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                <div className="bg-[#C9A0A0] py-5 text-center">
                  <span className="text-white tracking-[6px] text-xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>MAHRA</span>
                </div>
                <div className="p-6">
                  {displayName && <p className="text-lg text-[#1A1A1A] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Dear {displayName},</p>}
                  {form.body.split("\n").map((line, i) => (
                    <p key={i} className="text-sm text-[#4A4A4A] leading-relaxed mb-2">{line || <br />}</p>
                  ))}
                </div>
                <div className="bg-[#C9A0A0] py-4 text-center">
                  <span className="text-white tracking-[4px] text-sm" style={{ fontFamily: "'Cormorant Garamond', serif" }}>MAHRA</span>
                  <p className="text-white/60 text-[9px] tracking-wider mt-2">BORROW BEAUTIFUL · RETURN HAPPY</p>
                </div>
              </div>
            </div>
            <div
              className="sticky bottom-0 -mx-6 mt-4 px-6 py-3 bg-white border-t border-gray-100 flex flex-col-reverse sm:flex-row sm:justify-end gap-2"
              style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
            >
              <Button variant="outline" onClick={() => setPreview(false)} className="w-full sm:w-auto"><Edit3 className="w-3.5 h-3.5 mr-1.5" /> Edit</Button>
              <Button onClick={handleSend} disabled={sending} className="w-full sm:w-auto bg-[#9A7B4F] hover:bg-[#8a6b3f] text-white" data-testid="send-email-btn">
                <Send className="w-3.5 h-3.5 mr-1.5" /> {sending ? `Sending ${sendProgress.sent}/${sendProgress.total}...` : isBulk ? `Send to ${recipients.length}` : "Send Email"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-6 pb-0 space-y-4" data-testid="email-form">
            {isBulk ? (
              <div>
                <label className="text-xs text-gray-500 mb-1.5 block">To ({recipients.length} recipients)</label>
                <div className="flex flex-wrap gap-1.5 max-h-20 overflow-y-auto p-2 border border-gray-200 rounded-md bg-gray-50" data-testid="bulk-recipients">
                  {recipients.map(r => (
                    <span key={r.email} className="inline-flex items-center bg-white border border-gray-200 rounded px-2 py-0.5 text-[11px] text-gray-600">
                      {r.name ? `${r.name} <${r.email}>` : r.email}
                    </span>
                  ))}
                </div>
              </div>
            ) : (
              <>
                <div>
                  <RecipientPicker
                    channel="email"
                    selected={pickedRecipient}
                    onSelect={handlePick}
                    onClear={handleClearPick}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">To</label>
                  <Input value={form.to_email} onChange={e => handleToEmailChange(e.target.value)} placeholder="recipient@email.com" data-testid="email-to" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">From</label>
                  <select value={form.from_email} onChange={e => setForm(f => ({ ...f, from_email: e.target.value }))} className="w-full h-9 rounded-md border border-gray-200 px-3 text-sm bg-white" data-testid="email-from">
                    <option value="hello@shopmahra.com.au">hello@shopmahra.com.au</option>
                    <option value="support@shopmahra.com.au">support@shopmahra.com.au</option>
                  </select>
                </div>
              </div>
              </>
            )}
            {isBulk && (
              <div>
                <label className="text-xs text-gray-500 mb-1 block">From</label>
                <select value={form.from_email} onChange={e => setForm(f => ({ ...f, from_email: e.target.value }))} className="w-full h-9 rounded-md border border-gray-200 px-3 text-sm bg-white" data-testid="email-from">
                  <option value="hello@shopmahra.com.au">hello@shopmahra.com.au</option>
                  <option value="support@shopmahra.com.au">support@shopmahra.com.au</option>
                </select>
              </div>
            )}
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Subject</label>
              <Input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} placeholder="Email subject..." data-testid="email-subject" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Message</label>
              <textarea
                value={form.body}
                onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
                placeholder="Write your message here..."
                rows={8}
                className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#9A7B4F]/20 focus:border-[#9A7B4F]"
                data-testid="email-body"
              />
            </div>
            <div
              className="sticky bottom-0 -mx-6 -mb-0 px-6 py-3 bg-white border-t border-gray-100 flex flex-col-reverse sm:flex-row sm:justify-end gap-2"
              style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
            >
              <Button variant="outline" onClick={() => setPreview(true)} className="w-full sm:w-auto" data-testid="preview-btn"><Eye className="w-3.5 h-3.5 mr-1.5" /> Preview</Button>
              <Button onClick={handleSend} disabled={sending} className="w-full sm:w-auto bg-[#9A7B4F] hover:bg-[#8a6b3f] text-white" data-testid="send-email-btn">
                <Send className="w-3.5 h-3.5 mr-1.5" /> {sending ? `Sending ${sendProgress.sent}/${sendProgress.total}...` : isBulk ? `Send to ${recipients.length}` : "Send"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
